import { useCallback, useEffect, useRef, useState } from 'react';

import { LoggerFactory } from 'nordic/logger';

import DrawingHandler from '../lib/drawing-handler';
import { usePolygonStatus } from '../context/polygon-context';
import PolygonService from '../../../../services/polygon';

const log = LoggerFactory('map:use-polygon-drawing');

/**
 * Custom hook for handling polygon drawing on the map.
 *
 * Provides functionality for:
 * - Enabling/disabling polygon drawing mode
 * - Simplifying drawn polygons using the polygon service
 * - Rendering polygons on the map with predefined styles
 * - Clearing existing polygons
 * - Drawing polygons from specific coordinates
 *
 * @param {Object} params - Hook parameters
 * @param {google.maps.Map} params.map - Google Maps instance
 * @param {boolean} params.drawable - Indicates if drawing mode is active
 * @param {Function} [params.onPolygonComplete] - Callback executed when a polygon is completed
 * @param {Function} [params.onPolygonError] - Callback executed when an error occurs
 *
 * @returns {Object} Object with the following properties:
 * @returns {google.maps.Polygon|null} returns.polygon - Current polygon instance
 * @returns {Function} returns.clearPolygon - Function to clear the polygon from the map
 * @returns {Function} returns.drawPolygonFromCoordinates - Function to draw a polygon from coordinates
 *
 * @example
 * ```jsx
 * const { polygon, clearPolygon, drawPolygonFromCoordinates } = usePolygonDrawing({
 *   map: mapInstance,
 *   drawable: isDrawingMode,
 *   onPolygonComplete: (polygonLocation, polygon) => {
 *     console.log('Polygon completed:', polygonLocation);
 *   },
 *   onPolygonError: (error) => {
 *     console.error('Polygon error:', error);
 *   }
 * });
 * ```
 */
const usePolygonDrawing = ({ map, drawable, onPolygonComplete, onPolygonError }) => {
  const drawingHandlerRef = useRef(null);
  const [polygon, setPolygon] = useState(null);
  const { setPolygonCompleted } = usePolygonStatus();

  useEffect(() => {
    if (!map) {
      return;
    }

    if (drawable) {
      if (drawingHandlerRef.current) {
        drawingHandlerRef.current.cleanup();
      }

      drawingHandlerRef.current = new DrawingHandler(map, async (path) => {
        if (path.length < 3) {
          return;
        }

        try {
          const response = await PolygonService.simplifyPolygon(path);
          const { simplified_polygon: simplifiedPolygon, polygon_location: polygonLocation } = response;

          const newPolygon = new window.google.maps.Polygon({
            paths: simplifiedPolygon,
            strokeColor: '#3483FA',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#3483FA',
            fillOpacity: 0.35,
            clickable: false,
            map,
          });

          setPolygon(newPolygon);

          if (onPolygonComplete) {
            onPolygonComplete(polygonLocation, newPolygon);
          }
        } catch (error) {
          log.error('Error simplifying polygon:', error);

          if (onPolygonError) {
            onPolygonError(error);
          }
        }
      });

      drawingHandlerRef.current.start();
    } else if (drawingHandlerRef.current) {
      drawingHandlerRef.current.cleanup();
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (drawingHandlerRef.current) {
        drawingHandlerRef.current.cleanup();
      }
    };
  }, [map, drawable, onPolygonComplete, onPolygonError]);

  /**
   * Draws a polygon on the map using specific coordinates.
   *
   * @param {google.maps.Map} map - Map instance where to draw the polygon
   * @param {Array<Array<number>|Object>} coordinates - Array of polygon coordinates
   * @returns {google.maps.Polygon} Created polygon instance
   */
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const drawPolygonFromCoordinates = (map, coordinates) => {
    const path = coordinates.map((coord) => {
      if (Array.isArray(coord)) {
        const [lat, lng] = coord;

        return { lat, lng };
      }

      return { lat: coord.lat, lng: coord.lng };
    });

    // eslint-disable-next-line @typescript-eslint/no-shadow,no-undef
    const polygon = new google.maps.Polygon({
      paths: path,
      strokeColor: '#0044cc',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#0044cc',
      fillOpacity: 0.25,
      clickable: false,
      map,
    });

    setPolygon(polygon);

    return polygon;
  };

  /**
   * Clears the current polygon from the map and updates the state.
   */
  const clearPolygon = useCallback(() => {
    if (polygon) {
      polygon.setMap(null);
      setPolygon(null);
      setPolygonCompleted(false);
    }
  }, [polygon, setPolygonCompleted]);

  useEffect(() => {
    const handlePolygonClear = () => {
      clearPolygon();
    };

    window.addEventListener('polygon:clear', handlePolygonClear);

    return () => {
      window.removeEventListener('polygon:clear', handlePolygonClear);
    };
  }, [polygon, clearPolygon]);

  return { polygon, clearPolygon, drawPolygonFromCoordinates };
};

export default usePolygonDrawing;
